import type {
  CefrLevel,
  CoverageResponse,
  Dialog,
  DialogListResponse,
  AudioCacheResponse,
  AvatarCacheResponse,
} from "@/types/dialog";

const CONTENT_API =
  import.meta.env.VITE_CONTENT_API_URL ||
  "https://ojbr4cjpvh.execute-api.ap-northeast-1.amazonaws.com";

async function contentFetch<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(path, CONTENT_API);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value) url.searchParams.set(key, value);
    }
  }

  const response = await fetch(url.toString());
  if (!response.ok) {
    throw new Error(`Content API ${String(response.status)}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

async function contentPut<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(new URL(path, CONTENT_API).toString(), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Content API PUT ${String(response.status)}: ${await response.text()}`);
  }
  return response.json() as Promise<T>;
}

export interface FetchDialogsParams {
  language: string;
  level?: CefrLevel;
  limit?: number;
  cursor?: string;
}

export async function fetchDialogs(params: FetchDialogsParams): Promise<DialogListResponse> {
  return contentFetch<DialogListResponse>("/api/dialogs", {
    language: params.language,
    ...(params.level && { level: params.level }),
    ...(params.limit && { limit: String(params.limit) }),
    ...(params.cursor && { cursor: params.cursor }),
  });
}

export async function fetchDialog(slugOrId: string): Promise<Dialog> {
  return contentFetch<Dialog>(`/api/dialogs/${encodeURIComponent(slugOrId)}`);
}

export async function fetchCoverage(language?: string): Promise<CoverageResponse> {
  return contentFetch<CoverageResponse>("/api/coverage", {
    ...(language && { language }),
  });
}

export async function checkAudioCache(
  dialogId: string,
  languageCode: string,
  lineIndex: number,
): Promise<AudioCacheResponse> {
  return contentFetch<AudioCacheResponse>("/api/media/audio", {
    dialog_id: dialogId,
    language_code: languageCode,
    line: String(lineIndex),
  });
}

export async function saveAudioCache(
  dialogId: string,
  languageCode: string,
  lineIndex: number,
  audioUrl: string,
): Promise<void> {
  await contentPut("/api/media/audio", {
    dialog_id: dialogId,
    language_code: languageCode,
    line: lineIndex,
    audio_url: audioUrl,
  });
}

export async function checkAvatarCache(
  dialogId: string,
  languageCode: string,
  speakerIndex: number,
  emotion: string,
): Promise<AvatarCacheResponse> {
  return contentFetch<AvatarCacheResponse>("/api/media/avatar", {
    dialog_id: dialogId,
    language_code: languageCode,
    speaker: String(speakerIndex),
    emotion,
  });
}

const DIALOGS_API =
  import.meta.env.VITE_API_BASE_URL || "/api";

function getIdToken(): string | null {
  try {
    const raw = localStorage.getItem("CognitoIdentityServiceProvider.1mluir2p6j5pskl7ju0nplhuoq.LastAuthUser");
    if (!raw) return null;
    const key = `CognitoIdentityServiceProvider.1mluir2p6j5pskl7ju0nplhuoq.${raw}.idToken`;
    return localStorage.getItem(key);
  } catch { return null; }
}

export async function generateAudio(
  voice: string,
  text: string,
  dialogId: string,
  languageCode: string,
  lineIndex: number,
): Promise<{ audio_url: string }> {
  const response = await fetch(`${DIALOGS_API}/tts`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(getIdToken() ? { Authorization: getIdToken()! } : {}),
    },
    body: JSON.stringify({
      voice,
      text,
      dialog_id: dialogId,
      language_code: languageCode,
      line_index: lineIndex,
    }),
  });
  if (!response.ok) {
    throw new Error(`TTS ${String(response.status)}: ${await response.text()}`);
  }
  return response.json() as Promise<{ audio_url: string }>;
}

export async function saveAvatarCache(
  characterId: string,
  emotion: string,
  avatarUrl: string,
  assetId: string,
): Promise<void> {
  await contentPut("/api/media/avatar", {
    character_id: characterId,
    emotion,
    avatar_url: avatarUrl,
    asset_id: assetId,
  });
}
